import { useState } from "react";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import { auditReferenceId } from "@/components/auditLog/auditReference";
import { auditValueLabel } from "@/components/auditLog/auditValueLabel";
import type {
  AuditTextPage,
  AuditReferenceLabel,
  ChangeOperation,
  ComparisonSide,
} from "@/components/auditLog/auditLogTypes";
import styles from "@/components/auditLog/auditLog.module.css";

interface Props {
  page: AuditTextPage;
  side: ComparisonSide;
  onPage: (offset: number) => Promise<void>;
  onCopy: () => Promise<void>;
  neutral?: boolean;
  operation?: ChangeOperation;
  fieldName?: string;
  compact?: boolean;
  reference?: AuditReferenceLabel;
}

export default function AuditLogTextPage({
  page,
  side,
  onPage,
  onCopy,
  neutral,
  operation,
  fieldName,
  compact = false,
  reference,
}: Props) {
  const { t } = useTranslation();
  const [previousOffsets, setPreviousOffsets] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const changePage = async (offset: number, previous: number[]) => {
    setBusy(true);
    setStatus("");
    try {
      await onPage(offset);
      setPreviousOffsets(previous);
    } catch {
      setStatus(t("audit_diff_load_failed"));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    setBusy(true);
    try {
      await onCopy();
      setStatus(t("audit_diff_copied"));
    } catch {
      setStatus(t("audit_diff_copy_failed"));
    } finally {
      setBusy(false);
    }
  };
  const text = page.parts.map((part) => part.text).join("");
  const resolved =
    !neutral &&
    side !== "raw" &&
    page.kind === "string" &&
    page.offset === 0 &&
    page.end === page.total &&
    reference?.id === auditReferenceId(text)
      ? reference
      : undefined;
  const label =
    resolved?.label ??
    auditValueLabel(page.kind, text, t, {
      fieldName,
      literal:
        neutral ||
        side === "raw" ||
        page.offset !== 0 ||
        page.end !== page.total,
    });
  const parts =
    label !== text
      ? [{ text: label, changed: page.parts.some((part) => part.changed) }]
      : page.parts;
  const operationLabel =
    side === "before" && operation === "removed"
      ? `− ${t("audit_diff_removed")}`
      : side === "after" && operation === "added"
      ? `+ ${t("audit_diff_added")}`
      : t(`audit_diff_${side}`);
  const copyButton = (
    <GenericButton
      size="small"
      variant={compact ? "icon" : "outlined"}
      color={compact ? "iconBase" : "primary"}
      className={compact ? styles.copyIcon : undefined}
      aria-label={t("audit_diff_copy")}
      title={t("audit_diff_copy")}
      disabled={busy}
      onClick={copy}
    >
      {compact ? <ContentCopyIcon fontSize="inherit" /> : t("audit_diff_copy")}
    </GenericButton>
  );
  return (
    <section
      className={compact ? styles.compactValue : styles.valuePanel}
      aria-label={t(`audit_diff_${side}`)}
      aria-busy={busy}
    >
      {compact ? (
        <span className={styles.visuallyHidden}>{operationLabel}</span>
      ) : (
        <div className={styles.valueHeader}>
          <strong>{operationLabel}</strong>
          {copyButton}
        </div>
      )}
      <div className={compact ? styles.compactValueLine : undefined}>
        <pre className={styles.valueText} tabIndex={0}>
          {parts.map((part, index) => (
            <span
              key={index}
              className={
                part.changed && !neutral
                  ? side === "before"
                    ? styles.removed
                    : styles.added
                  : undefined
              }
            >
              {resolved?.subjectId ? (
                <NavigateToSubjectOverviewLink
                  subjectId={resolved.subjectId}
                  label={part.text}
                  skipGlobalStateUpdate
                  className={styles.referenceLink}
                />
              ) : (
                part.text
              )}
            </span>
          ))}
        </pre>
        {compact && copyButton}
      </div>
      {(page.offset > 0 || page.end < page.total) && (
        <div className={styles.pager}>
          <GenericButton
            size="small"
            variant="outlined"
            disabled={busy || previousOffsets.length === 0}
            onClick={() =>
              changePage(
                previousOffsets[previousOffsets.length - 1],
                previousOffsets.slice(0, -1)
              )
            }
          >
            {t("audit_diff_previous")}
          </GenericButton>
          <span>
            {t("audit_diff_character_range", {
              start: page.offset + 1,
              end: page.end,
              total: page.total,
            })}
          </span>
          <GenericButton
            size="small"
            variant="outlined"
            disabled={busy || page.end >= page.total}
            onClick={() =>
              changePage(page.end, [...previousOffsets, page.offset])
            }
          >
            {t("audit_diff_next")}
          </GenericButton>
        </div>
      )}
      <span className={styles.hint} role="status">
        {status}
      </span>
    </section>
  );
}
